// ModalShell — Presentational outer chrome for the EditTripModal: the
// fixed full-screen overlay/backdrop, the centered white modal container, and
// the header row (the "Edit Trip" title with its Edit3 icon, the "Trip Name"
// label + controlled text input, and the close "X" button). The modal body is
// supplied via {children} so the container (task 10.7) can compose the Trip
// Info header, Add Events section, Business Meetings section, and footer inside
// in the same order the source rendered them.
//
// Extracted verbatim from `EditTripModal.js` (the outer overlay <div>, the
// inner container <div>, and the "Header" block) as part of the
// behavior-preserving refactor (issue #240). The trip-name <input> physically
// lives inside the header in the source DOM, so it is kept here (next to the
// title, before the close button) to preserve the exact element hierarchy the
// characterization snapshots captured. Render-only, wrapped in React.memo, with
// NO internal state — the trip-name value, its change handler, and the close
// handler are all passed in via props. Styles, the placeholder
// ("Enter trip name..."), the inline focus/blur/hover handlers, icons
// (lucide-react), and attributes are identical to the source.
//
// Props:
//   tripName          - string; the controlled value of the trip-name input
//   onTripNameChange  - (value) => void invoked on trip-name input change
//   onClose           - () => void invoked when the close "X" button is clicked
//   children          - the modal body content rendered after the header
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { X, Edit3 } from 'lucide-react';

export const ModalShell = React.memo(function ModalShell({
  tripName,
  onTripNameChange,
  onClose,
  children
}) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <Edit3 size={20} style={{ color: '#0ea5e9' }} />
              Edit Trip
            </h2>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                minWidth: '80px'
              }}>
                Trip Name:
              </label>
              <input
                type="text"
                value={tripName || ''}
                onChange={(e) => onTripNameChange(e.target.value)}
                placeholder="Enter trip name..."
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#1f2937',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0ea5e9';
                  e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              padding: '4px',
              borderRadius: '6px',
              transition: 'color 0.2s',
              marginLeft: '16px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#6b7280'}
            onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
          >
            <X size={20} />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
});

export default ModalShell;
